/* global window URLSearchParams */
import jwtDecode from 'jwt-decode';
import { StorageAdapter } from '../../../../src/browser/utils/StorageAdapter.ts';

let storageWatchers = {};

export const expiredTokenHash = '#tokenExpired';

export default class Token {
	static get() {
		let browserToken;
		if (window.location.search.indexOf('token=') !== -1) {
			/* ignore URL starting with /channel-granted as sometime OAuth uses ?token too */
			if (!window.location.pathname.startsWith('/channel-granted')) {
				const searchParams = new URLSearchParams(window.location.search);
				if (searchParams.has("token")) {
					browserToken = searchParams.get("token");
				}
			}
		} else if (window.location.search.indexOf('authToken=') !== -1) {
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has("authToken")) {
                browserToken = searchParams.get("authToken");
            }
		}

		// Try synchronous storage first (for backward compatibility)
		let token = StorageAdapter.getSync("userToken");

		if (browserToken) {
			if (!token) {
				// Async set - don't wait for it
				StorageAdapter.set("userToken", browserToken);
			}
			return browserToken;
		}

		if (!token && window['config'] && window['config']['token']) {
			token = window['config']['token'];
		}

		if (!token && window['Dativery'] && window['Dativery']['token']) {
			token = window['Dativery']['token'];
		}

		return token;
	}

	// Async version for contexts where you can await
	static async getAsync() {
		let browserToken;
		if (window.location.search.indexOf('token=') !== -1) {
			if (!window.location.pathname.startsWith('/channel-granted')){
				const searchParams = new URLSearchParams(window.location.search);
				if (searchParams.has("token")) {
					browserToken = searchParams.get("token");
				}
			}
		}

		// Try async storage (works in all contexts including iframes)
		let token = await StorageAdapter.get("userToken");

		if (browserToken) {
			if (!token) {
				await StorageAdapter.set("userToken", browserToken);
			}
			return browserToken;
		}

		if (!token && window['config'] && window['config']['token']) {
			token = window['config']['token'];
		}

		if (!token && window['Dativery'] && window['Dativery']['token']) {
			token = window['Dativery']['token'];
		}

		return token;
	}

	static remove() {
		// Async remove - don't wait for it
		StorageAdapter.removeSync('userToken');
		StorageAdapter.removeSync('profile');
	}

	static set(token) {
		// Async set - don't wait for it
		StorageAdapter.setSync('userToken', token);
	}

	static async removeAsync() {
		await StorageAdapter.remove('userToken');
		await StorageAdapter.remove('profile');
	}

	static async setAsync(token) {
		await StorageAdapter.set('userToken', token);
	}

	static isExpired() {
		return Token.getExpiration() <= 0;
	}

	static getDecoded() {
		try {
			let token = Token.get();

			if (token) {
				return jwtDecode(token);
			}

			return undefined;
		} catch (e) {
			console.error("Dativery: Error parsing token", e);
		}
	}

	static getExpiration() {
		let expiresIn = 0;
		let decodedToken = Token.getDecoded();

		if (!decodedToken) {
			return 0;
		}

		expiresIn = (decodedToken.exp * 1000) - (new Date().getTime());

		if (expiresIn <= 0) {
			return 0;
		}

		return expiresIn;
	}

	static watcherEvent(event) {
		if (event.key === "userToken") {
			Object.keys(storageWatchers).forEach(callbackKey => {
				try {
					storageWatchers[callbackKey]();
				}
				catch (e) {
					console.error(e);
					Token.stopWatch(storageWatchers[callbackKey]);
				}
			});
		}
	}

	// chrome.storage change listener
	static chromeStorageListener(changes, areaName) {
		if (areaName === 'local' && changes.userToken) {
			Object.keys(storageWatchers).forEach(callbackKey => {
				try {
					storageWatchers[callbackKey]();
				}
				catch (e) {
					console.error(e);
					Token.stopWatch(storageWatchers[callbackKey]);
				}
			});
		}
	}

	static startWatch(callback) {
		if (!storageWatchers[String(callback)]) {
			storageWatchers[String(callback)] = callback;

			// if this is the first added callback, start the watcher
			if (Object.keys(storageWatchers).length === 1) {
				window.addEventListener('storage', Token.watcherEvent);
				
				// Also watch chrome.storage if available
				if (StorageAdapter.isExtensionContext()) {
					try {
						chrome.storage.onChanged.addListener(Token.chromeStorageListener);
					} catch (e) {
						console.error('Dativery: Error adding chrome.storage listener', e);
					}
				}
			}
		}
	}

	static stopWatch(callback) {
		if (storageWatchers[String(callback)]) {
			delete storageWatchers[String(callback)];
		}

		if (Object.keys(storageWatchers).length === 0) {
			window.removeEventListener('storage', Token.watcherEvent);
			
			// Also remove chrome.storage listener if available
			if (StorageAdapter.isExtensionContext()) {
				try {
					chrome.storage.onChanged.removeListener(Token.chromeStorageListener);
				} catch (e) {
					console.error('Dativery: Error removing chrome.storage listener', e);
				}
			}
		}
	}
}
